import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BomPartType } from 'bom/enums';
import { ActionButton } from 'core/components';
import { ActionButtonType } from 'core/enums';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { QuoteOrderPricingType } from 'quotes/enums';
import { IQuotePart, IVendorQuote } from 'quotes/types';
import { getActiveQuoteParts } from 'quotes/utils';
import { Color, Variant } from 'theme/enums';

import { CreateVendorOrderFromQuoteForm } from './CreateVendorOrderFromQuote';
import { OrderVendorQuoteConfirmation } from './OrderVendorQuoteConfirmation';

interface IProps {
  quote: IVendorQuote;
}

export const OrderVendorQuoteButton = ({ quote }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false);
  const [orderPricingType, setOrderPricingType] = useState<QuoteOrderPricingType | null>(null);

  const { t } = useTranslation();

  const { isLoadingTopNotificationDisplayed } = useNotificationContext();
  const { rows, isDataLoadCompleted } = useAgGridClientTableContext();

  const activeParts = getActiveQuoteParts(rows as IQuotePart[]);

  const isOrderEnabled =
    isDataLoadCompleted &&
    !isLoadingTopNotificationDisplayed &&
    activeParts?.some((part: IQuotePart) => BomPartType.PART === part.type);

  const onOrder = (orderPricingType: QuoteOrderPricingType) => {
    setOrderPricingType(orderPricingType);
    setIsConfirmationOpen(false);
    setIsOrderFormOpen(true);
  };

  return (
    <>
      <ActionButton
        action={{
          label: t('buttonLabelOrderAll'),
          type: ActionButtonType.BUTTON,
          color: Color.SUCCESS,
          variant: isOrderEnabled ? Variant.SOLID : Variant.OUTLINED,
          onClick: () => setIsConfirmationOpen(true),
          disabled: !isOrderEnabled,
        }}
      />

      {isConfirmationOpen && (
        <OrderVendorQuoteConfirmation
          activeParts={activeParts}
          onConfirm={onOrder}
          onCancel={() => setIsConfirmationOpen(false)}
        />
      )}

      <CreateVendorOrderFromQuoteForm
        isOpen={isOrderFormOpen}
        onClose={() => setIsOrderFormOpen(false)}
        quote={quote}
        orderPricingType={orderPricingType}
      />
    </>
  );
};
